<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('codeFormatter.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('codeFormatter.title') }}</h1>
      <p>{{ $t('codeFormatter.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="actions">
        <div class="lang-tabs">
          <button v-for="lang in languages" :key="lang.key"
            :class="['tab-btn', { active: currentLang === lang.key }]"
            @click="currentLang = lang.key">
            {{ lang.label }}
          </button>
        </div>
        <div class="action-btns">
          <button class="btn primary" @click="format">{{ $t('codeFormatter.format') }}</button>
          <button class="btn" @click="compress">{{ $t('codeFormatter.compress') }}</button>
          <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
          <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
        </div>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>
      <div v-if="errorMessage" class="alert error">{{ errorMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('codeFormatter.inputLabel') }}</label>
          <textarea
            v-model="input"
            :placeholder="placeholders[currentLang]"
            spellcheck="false"
          ></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('codeFormatter.outputLabel') }}</label>
          <textarea
            v-model="output"
            readonly
            :placeholder="$t('codeFormatter.outputPlaceholder')"
            spellcheck="false"
          ></textarea>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('codeFormatter.whatIs') }}</h2>
      <p>{{ $t('codeFormatter.whatIsDesc') }}</p>
      <h2>{{ $t('codeFormatter.featuresTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('codeFormatter.features')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CodeFormatter',
  data() {
    return {
      input: '',
      output: '',
      currentLang: 'css',
      successMessage: '',
      errorMessage: ''
    }
  },
  computed: {
    languages() {
      return [
        { key: 'css', label: 'CSS' },
        { key: 'javascript', label: 'JavaScript' },
        { key: 'html', label: 'HTML' },
        { key: 'sql', label: 'SQL' }
      ]
    },
    placeholders() {
      return {
        css: 'body{margin:0;padding:0;}.container{width:100%;max-width:1200px;}',
        javascript: 'function hello(name){const msg="Hello, "+name;console.log(msg);return msg;}',
        html: '<div class="container"><h1>Title</h1><p>Content</p></div>',
        sql: 'SELECT id, name, email FROM users WHERE status = 1 AND age > 18 ORDER BY created_at DESC LIMIT 10'
      }
    }
  },
  methods: {
    format() {
      this.errorMessage = ''
      if (!this.input.trim()) return
      try {
        switch (this.currentLang) {
          case 'css': this.output = this.formatCss(this.input); break
          case 'javascript': this.output = this.formatJs(this.input); break
          case 'html': this.output = this.formatHtml(this.input); break
          case 'sql': this.output = this.formatSql(this.input); break
        }
        this.successMessage = this.$t('codeFormatter.formatSuccess')
        setTimeout(() => { this.successMessage = '' }, 2000)
      } catch (e) {
        this.errorMessage = this.$t('codeFormatter.formatError') + ': ' + e.message
      }
    },
    compress() {
      this.errorMessage = ''
      if (!this.input.trim()) return
      try {
        switch (this.currentLang) {
          case 'css': this.output = this.compressCss(this.input); break
          case 'javascript': this.output = this.compressJs(this.input); break
          case 'html': this.output = this.compressHtml(this.input); break
          case 'sql': this.output = this.compressSql(this.input); break
        }
        this.successMessage = this.$t('codeFormatter.compressSuccess')
        setTimeout(() => { this.successMessage = '' }, 2000)
      } catch (e) {
        this.errorMessage = e.message
      }
    },
    // CSS 格式化
    formatCss(code) {
      let result = code
        .replace(/\s*{\s*/g, ' {\n  ')
        .replace(/\s*}\s*/g, '\n}\n\n')
        .replace(/;\s*/g, ';\n  ')
        .replace(/,\s*/g, ',\n')
        .replace(/\n\s*\n\s*\n/g, '\n\n')
      // 清理每行尾部多余空格
      result = result.split('\n').map(line => line.trimEnd()).join('\n')
      // 修复 } 前的缩进
      result = result.replace(/\n\s*\n\s*}/g, '\n}')
      return result.trim()
    },
    compressCss(code) {
      return code
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\s+/g, ' ')
        .replace(/\s*{\s*/g, '{')
        .replace(/\s*}\s*/g, '}')
        .replace(/\s*;\s*/g, ';')
        .replace(/\s*:\s*/g, ':')
        .replace(/\s*,\s*/g, ',')
        .trim()
    },
    // JavaScript 格式化（简单缩进）
    formatJs(code) {
      let indent = 0
      const lines = []
      // 简单分词，按 { } ; 分行
      const tokens = code.replace(/\s+/g, ' ').trim()
      let current = ''
      for (let i = 0; i < tokens.length; i++) {
        const ch = tokens[i]
        if (ch === '{') {
          lines.push('  '.repeat(indent) + current.trim() + ' {')
          current = ''
          indent++
        } else if (ch === '}') {
          if (current.trim()) lines.push('  '.repeat(indent) + current.trim())
          current = ''
          indent = Math.max(0, indent - 1)
          lines.push('  '.repeat(indent) + '}')
        } else if (ch === ';') {
          lines.push('  '.repeat(indent) + current.trim() + ';')
          current = ''
        } else {
          current += ch
        }
      }
      if (current.trim()) lines.push('  '.repeat(indent) + current.trim())
      return lines.filter(l => l.trim()).join('\n')
    },
    compressJs(code) {
      return code
        .replace(/\/\/.*$/gm, '')
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\s+/g, ' ')
        .replace(/\s*([{}();,=+\-*/<>!&|?:])\s*/g, '$1')
        .trim()
    },
    // HTML 格式化
    formatHtml(code) {
      const selfClosing = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'])
      let indent = 0
      let result = ''
      // 将标签拆开
      const parts = code.replace(/>\s*</g, '>\n<').split('\n')
      for (let part of parts) {
        part = part.trim()
        if (!part) continue
        // 关闭标签
        if (/^<\//.test(part)) {
          indent = Math.max(0, indent - 1)
          result += '  '.repeat(indent) + part + '\n'
        } else if (/^<[a-zA-Z]/.test(part)) {
          result += '  '.repeat(indent) + part + '\n'
          const tagMatch = part.match(/^<(\w+)/)
          if (tagMatch && !selfClosing.has(tagMatch[1].toLowerCase()) && !part.endsWith('/>') && !/^<.*\/>$/.test(part)) {
            indent++
          }
        } else {
          result += '  '.repeat(indent) + part + '\n'
        }
      }
      return result.trim()
    },
    compressHtml(code) {
      return code
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/>\s+</g, '><')
        .replace(/\s+/g, ' ')
        .trim()
    },
    // SQL 格式化
    formatSql(code) {
      const keywords = ['SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'ORDER BY', 'GROUP BY', 'HAVING', 'LIMIT', 'OFFSET', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'ON', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM', 'CREATE TABLE', 'ALTER TABLE', 'DROP TABLE', 'UNION', 'UNION ALL', 'AS', 'IN', 'NOT IN', 'BETWEEN', 'LIKE', 'IS NULL', 'IS NOT NULL', 'EXISTS', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END']
      let result = code.trim()
      // 将所有关键字大写并换行
      for (const kw of keywords.sort((a, b) => b.length - a.length)) {
        const regex = new RegExp('\\b' + kw.replace(/ /g, '\\s+') + '\\b', 'gi')
        result = result.replace(regex, '\n' + kw)
      }
      // 缩进非关键字行
      const lines = result.split('\n').filter(l => l.trim())
      const mainKeywords = new Set(['SELECT', 'FROM', 'WHERE', 'ORDER BY', 'GROUP BY', 'HAVING', 'LIMIT', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM', 'CREATE TABLE', 'UNION', 'UNION ALL'])
      const formatted = lines.map(line => {
        const trimmed = line.trim()
        const firstWord = trimmed.split(/\s+/).slice(0, 2).join(' ')
        if (mainKeywords.has(firstWord) || mainKeywords.has(trimmed.split(/\s+/)[0])) {
          return trimmed
        }
        return '  ' + trimmed
      })
      return formatted.join('\n')
    },
    compressSql(code) {
      return code.replace(/\s+/g, ' ').trim()
    },
    async copyResult() {
      if (!this.output) return
      try {
        await navigator.clipboard.writeText(this.output)
        this.successMessage = this.$t('common.copied')
      } catch {
        const ta = document.createElement('textarea')
        ta.value = this.output; ta.style.position = 'fixed'; ta.style.left = '-9999px'
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
        this.successMessage = this.$t('common.copied')
      }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() {
      this.input = ''
      this.output = ''
      this.successMessage = ''
      this.errorMessage = ''
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.actions { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; justify-content: space-between; align-items: center; }
.lang-tabs { display: flex; gap: 4px; background: var(--bg-tertiary); border-radius: 8px; padding: 4px; }
.tab-btn { background: none; border: none; color: var(--text-secondary); padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 600; transition: all 0.2s; }
.tab-btn.active { background: var(--accent-color); color: white; }
.tab-btn:hover:not(.active) { background: var(--bg-hover); }
.action-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 8px 16px; cursor: pointer; font-size: 13px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.alert.error { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #ef4444; }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } .actions { flex-direction: column; } }
.editor-panel { display: flex; flex-direction: column; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; }
.editor-panel textarea { min-height: 350px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.5; resize: vertical; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
